import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useActorEmails, fmtAuthor } from "@/hooks/use-actor-emails";

export const Route = createFileRoute("/_authenticated/admin/equipo")({
  
  head: () => ({ meta: [{ title: "Admin — Equipo | Mine Class" }, { name: "description", content: "Panel de administración de Equipo — acceso restringido a Mine Class." }, { name: "robots", content: "noindex, nofollow" }] }),
  component: EquipoAdmin,
});

type Photo = {
  id: string;
  image_url: string;
  alt: string;
  sort_order: number;
  is_active: boolean;
  created_by: string | null;
  updated_by: string | null;
};

const empty: Partial<Photo> = {
  image_url: "",
  alt: "",
  sort_order: 0,
  is_active: true,
};

function EquipoAdmin() {
  const [items, setItems] = useState<Photo[]>([]);
  const [editing, setEditing] = useState<Partial<Photo> | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    const { data } = await supabase
      .from("team_photos")
      .select("*")
      .order("sort_order")
      .order("created_at");
    setItems((data ?? []) as Photo[]);
  }
  useEffect(() => { load(); }, []);

  async function uploadImage(file: File): Promise<string> {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    const path = `${userId}/equipo/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
    const { error } = await supabase.storage.from("media").upload(path, file, { contentType: file.type });
    if (error) throw error;
    const { data } = await supabase.storage.from("media").createSignedUrl(path, 60 * 60 * 24 * 365 * 5);
    return data?.signedUrl ?? "";
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setBusy(true);
    setErr(null);
    try {
      const url = await uploadImage(file);
      setEditing({ ...editing, image_url: url });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Error subiendo imagen");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function save() {
    if (!editing) return;
    if (!editing.image_url) { setErr("La imagen es obligatoria."); return; }
    setErr(null);
    const payload = {
      image_url: editing.image_url,
      alt: editing.alt || "",
      sort_order: editing.sort_order ?? 0,
      is_active: !!editing.is_active,
    };
    const q = editing.id
      ? supabase.from("team_photos").update(payload).eq("id", editing.id)
      : supabase.from("team_photos").insert(payload);
    const { error } = await q;
    if (error) { setErr(error.message); return; }
    setEditing(null);
    load();
  }

  async function remove(p: Photo) {
    if (!confirm("¿Eliminar esta foto del equipo?")) return;
    await supabase.from("team_photos").delete().eq("id", p.id);
    load();
  }

  const lookup = useActorEmails(items.flatMap((s) => [s.created_by, s.updated_by]));

  if (editing) {
    return (
      <div className="bg-white border border-[var(--color-outline-variant)] p-6 space-y-4 max-w-3xl">
        <h2 className="font-bold text-lg">{editing.id ? "Editar" : "Nueva"} foto del equipo</h2>
        <p className="text-xs text-[var(--color-on-surface-variant)]">
          Recomendado: <strong>1600 × 1000 px</strong> aproximadamente (formato horizontal).
        </p>

        <Field label="Imagen">
          <div className="space-y-2">
            {editing.image_url && (
              <div className="aspect-[16/10] bg-[var(--color-surface-container-low)] overflow-hidden">
                <img src={editing.image_url} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} disabled={busy} className="text-sm" />
            {busy && <p className="text-xs">Subiendo...</p>}
            <input
              value={editing.image_url ?? ""}
              onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
              placeholder="…o pega una URL"
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2 text-xs font-mono"
            />
          </div>
        </Field>

        <Field label="Descripción alternativa (accesibilidad)">
          <input
            value={editing.alt ?? ""}
            onChange={(e) => setEditing({ ...editing, alt: e.target.value })}
            placeholder="Ej: Equipo Mine Class en visita a mina"
            className="w-full border border-[var(--color-outline-variant)] px-3 py-2"
          />
        </Field>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Orden">
            <input type="number" value={editing.sort_order ?? 0}
              onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })}
              className="w-full border border-[var(--color-outline-variant)] px-3 py-2" />
          </Field>
          <label className="flex items-end gap-2 text-sm pb-2">
            <input type="checkbox" checked={!!editing.is_active}
              onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
            Activa (visible en la galería)
          </label>
        </div>

        {err && <p className="text-sm text-red-600">{err}</p>}
        <div className="flex gap-2">
          <button onClick={save} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">Guardar</button>
          <button onClick={() => setEditing(null)} className="px-4 py-2 border border-[var(--color-outline-variant)]">Cancelar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-bold text-lg">Fotos del equipo ({items.length})</h2>
          <p className="text-xs text-[var(--color-on-surface-variant)]">
            Aparecen en el carrusel de <span className="font-mono">/quienes-somos/equipo</span>. La primera foto (menor orden) se muestra como portada.
          </p>
        </div>
        <button onClick={() => setEditing({ ...empty })} className="px-4 py-2 bg-[var(--color-brand-coral)] text-white font-bold">
          + Nueva foto
        </button>
      </div>
      <div className="grid gap-4">
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-[var(--color-outline-variant)] p-4 flex gap-4">
            <div className="w-48 aspect-[16/10] bg-[var(--color-surface-container-low)] flex-shrink-0 overflow-hidden">
              {s.image_url && <img src={s.image_url} alt={s.alt} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">{s.alt || <span className="italic text-[var(--color-on-surface-variant)]">Sin descripción</span>}</h3>
              <div className="text-xs mt-2">
                Orden: {s.sort_order} · {s.is_active ? <span className="text-green-700 font-bold">Activa</span> : <span className="text-[var(--color-on-surface-variant)]">Oculta</span>}
              </div>
              <div className="text-[11px] text-[var(--color-on-surface-variant)] mt-1">
                {fmtAuthor(lookup, s.created_by, s.updated_by)}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => setEditing(s)} className="text-xs underline">Editar</button>
              <button onClick={() => remove(s)} className="text-xs text-red-600">Eliminar</button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-12 text-[var(--color-on-surface-variant)] bg-white border border-[var(--color-outline-variant)]">
            Aún no hay fotos. La página del equipo usará las imágenes por defecto.
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-widest text-[var(--color-on-surface-variant)] mb-1">{label}</div>
      {children}
    </label>
  );
}
